import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebounce } from 'use-debounce'
import { CardError } from 'components'
import {
  MultiSelectField,
  SelectField,
} from '@politechdev/blocks-design-system'
import { useRequest } from 'hooks'
import { fetchUsers } from 'requests/users'
import { buildMultiOptions, buildSingleOptions } from 'utils/select'
import { useCurrent } from 'contexts/index'
import { buildParams, buildOption, buildUser } from './utils'
import { SelectableUser, UserOption } from './types'

type BaseSelectProps = {
  label: string
  filters?: unknown[]
  [key: string]: unknown
}

type SingleSelectProps = {
  isMulti?: false | undefined
  user: SelectableUser | undefined
  onSelect: (user: SelectableUser | undefined) => void
} & BaseSelectProps

type MultiSelectProps = {
  isMulti: true
  users: SelectableUser[]
  onSelect: (users: SelectableUser[]) => void
} & BaseSelectProps

const UserSelectField = ({
  label,
  onSelect,
  user,
  users,
  isMulti,
  filters,
  ...props
}: SingleSelectProps | MultiSelectProps) => {
  const { t } = useTranslation()

  const [options, setOptions] = useState<UserOption[]>([])
  const [errorMsg, setErrorMsg] = useState<string | undefined>()

  const [value, setValue] = useState<number | undefined>(
    !isMulti && user ? user.id : undefined
  )
  const [multiValue, setMultiValue] = useState<number[]>(
    // eslint-disable-next-line @typescript-eslint/no-shadow
    isMulti ? users.map(user => user.id) : []
  )

  const [query, setQuery] = useState<string>('')
  const [debounced] = useDebounce(query, 300)
  const { doesCurrentUserHavePermission } = useCurrent()
  const hasPermission = doesCurrentUserHavePermission({
    resource: 'user',
    ability: 'view',
  })

  const { makeRequest, isLoading } = useRequest(fetchUsers, {
    onSuccess: ({ users: incomingUsers }) => {
      setOptions(incomingUsers.map(buildOption))
    },
    onError: () => {
      setErrorMsg('failed to fetch users')
    },
  })

  const getUsers = () => {
    const params = buildParams({ query: debounced, filters })

    if (hasPermission) {
      void makeRequest(params)
    }
  }

  useEffect(() => {
    getUsers()
  }, [debounced])

  useEffect(() => {
    if (!isMulti) {
      setValue(user?.id)
    }
  }, [user])

  useEffect(() => {
    if (isMulti) {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      setMultiValue(users.map(user => user.id))
    }
  }, [JSON.stringify(users)])

  const singleOptions = !isMulti
    ? buildSingleOptions(options, user, buildOption)
    : []

  const multiOptions = isMulti
    ? buildMultiOptions(options, users, buildOption)
    : []

  const handleChange = (selection: number) => {
    if (!isMulti) {
      setValue(selection)
      const selectedOption = singleOptions.find(
        // eslint-disable-next-line @typescript-eslint/no-shadow
        ({ value }) => value === selection
      )
      onSelect(selectedOption ? buildUser(selectedOption) : undefined)
    }
  }

  const handleMultiChange = (selections: number[]) => {
    if (isMulti) {
      onSelect(
        multiOptions
          // eslint-disable-next-line @typescript-eslint/no-shadow
          .filter(({ value }) => selections.includes(value))
          .map(buildUser)
      )
    }
  }

  return (
    <>
      <CardError hide={!errorMsg} message={errorMsg} />
      {isMulti ? (
        <MultiSelectField
          label={label || t('User')}
          options={multiOptions}
          onSelect={handleMultiChange}
          onInputChange={inputValue => setQuery(inputValue ?? '')}
          values={multiValue}
          loading={isLoading}
          disabled={!hasPermission}
          hint={
            !hasPermission
              ? t('You do not have permission to edit this field')
              : undefined
          }
          icon={undefined}
          {...props}
        />
      ) : (
        <SelectField
          label={label || t('User')}
          options={singleOptions}
          onSelect={handleChange}
          onInputChange={inputValue => setQuery(inputValue ?? '')}
          value={value}
          loading={isLoading}
          disabled={!hasPermission}
          hint={
            !hasPermission
              ? t('You do not have permission to edit this field')
              : undefined
          }
          {...props}
        />
      )}
    </>
  )
}

UserSelectField.defaultProps = {
  id: 'user',
  filters: [],
  user: null,
}

export default UserSelectField
